import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BATCH_DURATION, BATCH_SESSIONS, BATCH_MODE } from '../constants';

const COURSE_DATA = {
  python: {
    title: 'Python + AI Course',
    icon: <img src="/python.png" alt="Python Icon" style={{ width: 48, height: 48 }} />,
    tagline: 'From Zero to Python Pro — in 6–8 Weeks',
    overview:
      'No prior coding experience needed. We start from the very basics — what a program is — and build up to writing real programs, solving logic puzzles, and exploring AI tools. You will leave confident and capable.',
    color1: '#1B3A6B',
    color2: '#2A5298',
    topics: [
      'Python syntax, variables & data types',
      'Input/output, conditionals & loops',
      'Functions, scope & reusability',
      'Lists, dictionaries & data structures',
      'File handling & external modules',
      'Introduction to AI tools & APIs',
      'Simple automation scripts',
      'Debugging & code review best practices',
    ],
    projects: [
      ['🧮', 'Calculator App'],
      ['🎮', 'Number Guessing Game'],
      ['📊', 'Student Grade Tracker'],
      ['🤖', 'Simple Chatbot'],
      ['📁', 'File Organizer Script'],
      ['📈', 'Data Analyser'],
    ],
    tools: ['Python 3', 'VS Code', 'Jupyter Notebook', 'Git Basics', 'OpenAI API (intro)'],
    who: ['Students after 10th or Intermediate', 'Anyone with zero coding background', 'Students interested in data, AI, or backend'],
  },
  webdev: {
    title: 'Web Development Course',
    icon: '🌐',
    tagline: 'Build Beautiful Websites from Scratch — No Experience Needed',
    overview:
      'Learn the three pillars of the web — HTML, CSS, and JavaScript — and build real websites you can publish online and show to anyone. By week 8 you will have a portfolio website live on the internet.',
    color1: '#C41E3A',
    color2: '#e84363',
    topics: [
      'HTML5 structure, tags & semantic elements',
      'CSS styling — colors, fonts, spacing',
      'Flexbox & CSS Grid layouts',
      'Responsive design & media queries',
      'JavaScript fundamentals — variables, loops',
      'Functions, events & DOM manipulation',
      'Building interactive UI components',
      'Deploying a website to the internet (free)',
    ],
    projects: [
      ['🏠', 'Personal Portfolio'],
      ['📋', 'Contact Form Page'],
      ['🛍️', 'Product Showcase'],
      ['📰', 'Blog Layout'],
      ['🎯', 'Interactive Quiz App'],
      ['🖼️', 'Image Gallery'],
    ],
    tools: ['HTML5 / CSS3', 'JavaScript (ES6+)', 'VS Code', 'Chrome DevTools', 'GitHub Pages (deploy)'],
    who: ['Students after 10th or Intermediate', 'Anyone who wants to build websites', 'Future designers, entrepreneurs, or freelancers'],
  },
  msoffice: {
    title: 'MS Office & AI Tools',
    icon: '💼',
    tagline: 'From Computer Basics to Job-Ready Office Skills — in 6–8 Weeks',
    overview:
      'Start from absolute zero — even if you have never used a computer properly. We cover Windows basics, Microsoft Word, Excel, and PowerPoint in depth, then move to Google Workspace and modern AI tools like ChatGPT and Canva. These are the skills every employer expects.',
    color1: '#1a5a2e',
    color2: '#16a34a',
    topics: [
      'Computer basics — Windows, folders & shortcuts',
      'MS Word — documents, formatting & templates',
      'MS Word advanced — reports, mail merge & PDF export',
      'MS Excel — formulas, sorting & data entry',
      'MS Excel advanced — IF, VLOOKUP, charts & pivot tables',
      'MS PowerPoint — slides, animations & presenting',
      'Google Docs, Sheets & Forms — collaborate from anywhere',
      'AI tools — ChatGPT, Canva & Google Workspace productivity',
    ],
    projects: [
      ['📄', 'Resume in Word'],
      ['📑', 'Internship Report Template'],
      ['📊', 'Marks Sheet with Totals'],
      ['💰', 'Monthly Expense Tracker'],
      ['🎤', '10-Slide Presentation'],
      ['🗂️', 'Shared Budget Planner'],
    ],
    tools: ['MS Word', 'MS Excel', 'MS PowerPoint', 'Google Docs', 'Google Sheets', 'ChatGPT', 'Canva'],
    who: ['Students after 10th or Intermediate', 'Anyone who wants to get job-ready quickly', 'Students who want to use computers confidently'],
  },
};

export default function CoursePage({ course }) {
  const navigate = useNavigate();
  const d = COURSE_DATA[course];

  return (
    <div style={{ paddingTop: 64 }}>

      {/* Hero */}
      <section style={{
        background: `linear-gradient(135deg, ${d.color1}, ${d.color2})`,
        padding: '72px 24px 60px', color: '#fff',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -40, right: -40, width: 280, height: 280, background: 'rgba(240,180,41,0.1)', borderRadius: '50%' }} />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="badge" style={{ background: 'rgba(240,180,41,0.2)', color: '#F0B429', border: '1.5px solid rgba(240,180,41,0.38)' }}>
            ⏱ {BATCH_DURATION} · {BATCH_MODE}
          </div>
          <div style={{ fontSize: 60, margin: '14px 0 10px' }}>{d.icon}</div>
          <h1 className="serif" style={{ fontSize: 'clamp(28px, 5vw, 50px)', fontWeight: 800, marginBottom: 12 }}>
            {d.title}
          </h1>
          <p style={{ fontSize: 18, color: 'rgba(255,255,255,0.85)', marginBottom: 36, maxWidth: 560, lineHeight: 1.65 }}>
            {d.tagline}
          </p>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <button className="btn-gold" style={{ fontSize: 16 }} onClick={() => navigate('/register')}>
              📅 Register for Course
            </button>
            <button onClick={() => navigate('/register')} style={{
              background: 'rgba(255,255,255,0.15)', border: '2px solid rgba(255,255,255,0.4)',
              color: '#fff', padding: '13px 28px', borderRadius: 50,
              fontWeight: 800, fontSize: 15, cursor: 'pointer', fontFamily: 'Nunito, sans-serif',
              transition: 'background 0.2s',
            }}>
              Enroll Now →
            </button>
          </div>
        </div>
      </section>

      {/* Overview + Topics */}
      <section className="section">
        <div className="container">
          <div className="grid-2" style={{ gap: 48 }}>

            <div>
              <div className="badge">📖 Overview</div>
              <h2 className="section-title">What Is This Course?</h2>
              <p style={{ color: 'var(--gray)', fontSize: 16, lineHeight: 1.8, marginBottom: 28 }}>{d.overview}</p>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {[
                  ['⏱️', 'Duration', BATCH_DURATION],
                  ['📅', 'Sessions', BATCH_SESSIONS],
                  ['💻', 'Mode', BATCH_MODE],
                  ['🎓', 'Level', 'Beginner'],
                ].map(([icon, k, v]) => (
                  <div key={k} style={{ background: 'var(--cream)', borderRadius: 14, padding: 16 }}>
                    <div style={{ fontSize: 20, marginBottom: 4 }}>{icon}</div>
                    <div style={{ color: 'var(--gray)', fontSize: 12, fontWeight: 700 }}>{k}</div>
                    <div style={{ color: 'var(--navy)', fontWeight: 800, fontSize: 14, marginTop: 2 }}>{v}</div>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 28 }}>
                <div style={{ fontWeight: 800, color: 'var(--navy)', marginBottom: 10, fontSize: 15 }}>🛠️ Tools You Will Use</div>
                <div>{d.tools.map(t => <span key={t} className="tag">{t}</span>)}</div>
              </div>

              {/* Fees note */}
              <div style={{
                marginTop: 28, background: 'var(--gold-light)',
                border: '2px solid var(--gold)', borderRadius: 16, padding: '18px 20px',
                display: 'flex', gap: 12, alignItems: 'flex-start',
              }}>
                <span style={{ fontSize: 22 }}>💰</span>
                <div>
                  <div style={{ fontWeight: 800, color: 'var(--navy)', fontSize: 14, marginBottom: 4 }}>Fee Details</div>
                  <p style={{ color: 'rgba(27,58,107,0.75)', fontSize: 14, margin: 0, lineHeight: 1.6 }}>
                    Price details, combo offers, and payment options are available on the Registration Page.
                  </p>
                  <button
                    className="btn-primary"
                    style={{ marginTop: 12, fontSize: 13, padding: '9px 20px' }}
                    onClick={() => navigate('/register')}
                  >
                    View Fees & Register →
                  </button>
                </div>
              </div>
            </div>

            <div>
              <div className="badge">📚 Curriculum</div>
              <h2 className="section-title">What You Will Learn</h2>
              {d.topics.map(t => (
                <div key={t} className="check-row">
                  <div className="check-icon">✓</div>
                  <span>{t}</span>
                </div>
              ))}

              <div style={{ marginTop: 28, background: 'var(--gold-light)', border: '2px solid var(--gold)', borderRadius: 16, padding: 22 }}>
                <div style={{ fontWeight: 800, color: 'var(--navy)', marginBottom: 10, fontSize: 15 }}>🙋 Who Is This For?</div>
                {d.who.map(w => (
                  <div key={w} style={{ display: 'flex', gap: 8, marginBottom: 8, fontSize: 14, color: 'var(--navy)' }}>
                    <span style={{ color: 'var(--crimson)', fontWeight: 900 }}>→</span>{w}
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Projects */}
      <section className="section" style={{ background: 'var(--cream)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 44 }}>
            <div className="badge">🛠️ Projects</div>
            <h2 className="section-title" style={{ textAlign: 'center' }}>Projects You Will Build</h2>
            <p className="section-sub" style={{ margin: '0 auto', textAlign: 'center' }}>
              Real, functional projects that go into your portfolio and demonstrate actual skills.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16 }}>
            {d.projects.map(([icon, name]) => (
              <div key={name} className="card" style={{ textAlign: 'center', padding: '28px 16px' }}>
                <div style={{ fontSize: 32, marginBottom: 10 }}>{icon}</div>
                <div style={{ fontWeight: 800, color: 'var(--navy)', fontSize: 15 }}>{name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: 'var(--navy)', padding: '68px 24px', textAlign: 'center' }}>
        <div className="container">
          <h2 style={{ color: '#fff', fontSize: 32, fontWeight: 900, marginBottom: 12 }}>
            Ready to Start Your Journey?
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.78)', fontSize: 17, marginBottom: 36, lineHeight: 1.65 }}>
            Register now to join the batch. First class is treated as demo and fee can be paid in the first 3 classes.
          </p>
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="btn-gold" style={{ fontSize: 16, padding: '15px 40px' }} onClick={() => navigate('/register')}>
              📅 Register for Course
            </button>
            <button className="btn-primary" onClick={() => navigate('/fees')}>
              View Fees & Details →
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}